import { Form, Head } from "@inertiajs/react"
import { useTranslation } from "react-i18next"

import BrandSettingsLayout from "@/components/brand/settings/brand-settings-layout"
import HeadingSmall from "@/components/heading-small"
import InputError from "@/components/input-error"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import AppLayout from "@/layouts/app-layout"
import { brandSettingsThresholdsPath } from "@/routes"

/**
 * OKS-40 — Brand Settings → Lifecycle Thresholds page.
 *
 * Two number inputs:
 *   - `brand[rotation_threshold_months]`  (1..12, default 4)
 *   - `brand[inactivity_threshold_weeks]` (1..26, default 6)
 *
 * Helper copy explains the lifecycle alert each threshold drives.
 * Out-of-range values are rejected server-side via `ArgumentError`
 * raised by the model writer — surfaced as inline form errors.
 */

export interface BrandSettingsThresholdsProps {
  brand: {
    id: number
    rotation_threshold_months: number
    inactivity_threshold_weeks: number
  }
  ranges: {
    rotation_threshold_months: { min: number; max: number }
    inactivity_threshold_weeks: { min: number; max: number }
  }
  can_edit: boolean
}

export default function BrandSettingsThresholds({
  brand,
  ranges,
  can_edit,
}: BrandSettingsThresholdsProps) {
  const { t } = useTranslation()

  return (
    <AppLayout>
      <Head title={t("settings.brand.thresholds.title")} />
      <BrandSettingsLayout>
        <div data-testid="brand-settings-thresholds" className="space-y-6">
          <HeadingSmall
            title={t("settings.brand.thresholds.title")}
            description={t("settings.brand.thresholds.description")}
          />

          <Form
            method="patch"
            action={brandSettingsThresholdsPath()}
            options={{ preserveScroll: true }}
            className="space-y-6"
            data-testid="brand-settings-thresholds-form"
          >
            {({ errors, processing }) => (
              <>
                <div className="grid gap-2">
                  <Label htmlFor="rotation-threshold">
                    {t("settings.brand.thresholds.rotation_label")}
                  </Label>
                  <Input
                    id="rotation-threshold"
                    type="number"
                    name="brand[rotation_threshold_months]"
                    step={1}
                    inputMode="numeric"
                    defaultValue={brand.rotation_threshold_months}
                    readOnly={!can_edit}
                    aria-readonly={!can_edit}
                    aria-describedby="rotation-threshold-helper"
                    aria-invalid={
                      errors.rotation_threshold_months ? true : undefined
                    }
                    data-testid="brand-settings-thresholds-rotation"
                    className="max-w-[180px]"
                  />
                  <p
                    id="rotation-threshold-helper"
                    className="text-muted-foreground text-sm"
                  >
                    {t("settings.brand.thresholds.rotation_helper", {
                      min: ranges.rotation_threshold_months.min,
                      max: ranges.rotation_threshold_months.max,
                    })}
                  </p>
                  <InputError messages={errors.rotation_threshold_months} />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="inactivity-threshold">
                    {t("settings.brand.thresholds.inactivity_label")}
                  </Label>
                  <Input
                    id="inactivity-threshold"
                    type="number"
                    name="brand[inactivity_threshold_weeks]"
                    step={1}
                    inputMode="numeric"
                    defaultValue={brand.inactivity_threshold_weeks}
                    readOnly={!can_edit}
                    aria-readonly={!can_edit}
                    aria-describedby="inactivity-threshold-helper"
                    aria-invalid={
                      errors.inactivity_threshold_weeks ? true : undefined
                    }
                    data-testid="brand-settings-thresholds-inactivity"
                    className="max-w-[180px]"
                  />
                  <p
                    id="inactivity-threshold-helper"
                    className="text-muted-foreground text-sm"
                  >
                    {t("settings.brand.thresholds.inactivity_helper", {
                      min: ranges.inactivity_threshold_weeks.min,
                      max: ranges.inactivity_threshold_weeks.max,
                    })}
                  </p>
                  <InputError messages={errors.inactivity_threshold_weeks} />
                </div>

                {can_edit ? (
                  <Button
                    type="submit"
                    disabled={processing}
                    data-testid="brand-settings-thresholds-save"
                  >
                    {processing
                      ? t("settings.brand.thresholds.saving")
                      : t("settings.brand.thresholds.save")}
                  </Button>
                ) : (
                  <p
                    role="status"
                    data-testid="brand-settings-thresholds-readonly"
                    className="text-muted-foreground text-sm"
                  >
                    {t("settings.brand.thresholds.read_only_hint")}
                  </p>
                )}
              </>
            )}
          </Form>
        </div>
      </BrandSettingsLayout>
    </AppLayout>
  )
}
